/**
 * Shared presentation constants for the Celestia in-match screen — the
 * single source of truth for the wireframe P-label / phase-kind mapping and
 * seat-colour helpers consumed by BoardHeader, PhaseStack, and ActionDock.
 *
 * Wireframe: docs/wireframes/celestia/03-screen.html (`KIND` / `KCOL` script).
 */

import type { PhaseId } from "@cloudnest/celestia-engine";

export type PhaseKind = "decision" | "reaction" | "neutral";

/** Ordered P1→P10 sequence (W0 StartOfJourney is a transient pre-leg state). */
export const PHASE_ORDER: readonly PhaseId[] = [
  "AwaitSoloCaptainChoice",
  "AwaitRoll",
  "AwaitCaptainClaim",
  "AwaitPassengerDecision",
  "AwaitEjectionWindow",
  "AwaitCaptainDeclaration",
  "AwaitReactionWindow",
  "AwaitResolution",
  "AwaitTreasureChoice",
  "EndOfLeg",
  "EndOfJourney",
];

/** Short phase badge (the wireframe P1…P10 labels, P2b sub-window). */
export const PHASE_BADGE: Readonly<Record<PhaseId, string>> = {
  StartOfJourney: "P0",
  AwaitSoloCaptainChoice: "P1",
  AwaitRoll: "P2",
  AwaitCaptainClaim: "P2b",
  AwaitPassengerDecision: "P3",
  AwaitEjectionWindow: "P4",
  AwaitCaptainDeclaration: "P5",
  AwaitReactionWindow: "P6",
  AwaitResolution: "P7",
  AwaitTreasureChoice: "P8",
  EndOfLeg: "P9",
  EndOfJourney: "P10",
};

/** Wireframe `KIND`: each phase's accent kind. */
export const PHASE_KIND: Readonly<Record<PhaseId, PhaseKind>> = {
  StartOfJourney: "neutral",
  AwaitSoloCaptainChoice: "decision",
  AwaitRoll: "neutral",
  AwaitCaptainClaim: "decision",
  AwaitPassengerDecision: "decision",
  AwaitEjectionWindow: "reaction",
  AwaitCaptainDeclaration: "decision",
  AwaitReactionWindow: "reaction",
  AwaitResolution: "neutral",
  AwaitTreasureChoice: "neutral",
  EndOfLeg: "neutral",
  EndOfJourney: "neutral",
};

/** Wireframe `KCOL`: kind → accent CSS variable (drives `--kind`). */
export const KIND_COLOR: Readonly<Record<PhaseKind, string>> = {
  decision: "var(--cn-celestia-captain-ring)",
  reaction: "var(--cn-celestia-reaction-pulse)",
  neutral: "var(--cn-celestia-selection)",
};

/** `--kind` accent for the current phase (current card + dock header + ring). */
export function phaseAccent(phaseId: PhaseId): string {
  return KIND_COLOR[PHASE_KIND[phaseId] ?? "neutral"];
}

/** Seat index (0-based) → seat-colour token. Wraps the 6-colour palette. */
export function seatVar(seat: number): string {
  return `var(--cn-celestia-seat-${(((seat % 6) + 6) % 6) + 1})`;
}

/** Player label shown in dots / table / slots (1-based seat). */
export function seatLabel(seat: number): string {
  return `P${seat + 1}`;
}
